import { useState } from "react";
import { GripVertical } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/modal";
import { cn } from "@/lib/utils/cn";

export interface ActionOrderItem {
  id: string;
  title: string;
  subtitle: string;
  visible: boolean;
}

export interface ActionOrderRow {
  id: string;
  title: string;
  position: number;
  previous: number | null;
  is_new: boolean;
  moved: boolean;
}

export interface ActionOrderPayload {
  kind: string;
  action: { id: string; name: string; goal: string; issue: string };
  items: ActionOrderItem[];
  reorderable: boolean;
  empty_message: string;
  link_change_pending: boolean;
  can_review: boolean;
  pending: {
    id: string;
    proposed_by: string;
    initials: string;
    created_at: string;
    note: string;
    is_mine: boolean;
    rows: ActionOrderRow[];
    added_since: string[];
    dropped: number;
  } | null;
}

export interface OrderDecision {
  decision: "propose" | "approve" | "counter";
  ids?: string[];
  request_id?: string;
  note?: string;
}

interface OrderQuery {
  data?: ActionOrderPayload;
  isPending: boolean;
  isError: boolean;
}

interface OrderMutation {
  mutateAsync: (decision: OrderDecision) => Promise<unknown>;
  isPending: boolean;
}

/**
 * Per-action reorder dialog, shared by the tools & financial-aids back-offices
 * and the counterpart of the Django modal.
 *
 * Where a tool / aide financière appears for an enterprise belongs to the piste
 * d'action, so it is set here rather than on the resource. Saving does not
 * apply: it stages the order for a *different* administrator to approve or
 * counter-propose (four-eyes), enforced server-side. The i18n namespace (``ns``)
 * and the query/mutation hooks are injected so one implementation serves both
 * slices.
 */
export function LinkOrderDialog({
  action,
  ns,
  useOrder,
  useDecision,
  onClose,
}: {
  action: { id: string; name: string };
  ns: string;
  useOrder: (actionId: string) => OrderQuery;
  useDecision: (actionId: string) => OrderMutation;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const query = useOrder(action.id);
  const decide = useDecision(action.id);
  const [draft, setDraft] = useState<ActionOrderItem[] | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [countering, setCountering] = useState(false);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const payload = query.data;
  const pending = payload?.pending ?? null;
  // The draft only exists while the administrator is dragging; otherwise the
  // list is whatever the server last returned.
  const items = draft ?? payload?.items ?? [];
  const editable = Boolean(payload && (!pending || countering));
  // A drag that puts the row back where it started leaves nothing to propose —
  // compare the order, don't just ask whether a drag happened, or the server
  // answers the round trip with "L'ordre est inchangé." (matches the Django
  // modal, which compares the id lists).
  const changed =
    draft !== null &&
    draft.map((item) => item.id).join() !==
      (payload?.items ?? []).map((item) => item.id).join();

  const move = (from: number, to: number) => {
    const clamped = Math.max(0, Math.min(items.length - 1, to));
    if (from === clamped || from < 0) return;
    const next = items.slice();
    const [item] = next.splice(from, 1);
    if (!item) return;
    next.splice(clamped, 0, item);
    setDraft(next);
  };

  const submit = async (decision: OrderDecision) => {
    setError(null);
    try {
      await decide.mutateAsync(decision);
      setDraft(null);
      setCountering(false);
      setNote("");
    } catch (e) {
      // ``unwrap`` throws the normalized ApiError shape, not an Error — the
      // service's message (already localized) lands in ``detail``.
      const detail = (e as { detail?: string } | null)?.detail;
      setError(detail || t(`${ns}.error`));
    }
  };

  const startCounter = () => {
    // Counter-proposing starts from what was proposed, not from the live order.
    const byId = new Map((payload?.items ?? []).map((i) => [i.id, i]));
    setDraft(
      (pending?.rows ?? []).map(
        (row) =>
          byId.get(row.id) ?? {
            id: row.id,
            title: row.title,
            subtitle: "",
            visible: true,
          },
      ),
    );
    setCountering(true);
  };

  return (
    <Modal
      title={t(`${ns}.linkOrder.title`)}
      onClose={onClose}
      className="max-w-xl"
    >
      <div className="flex flex-col gap-3">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">
          {[payload?.action.issue, payload?.action.goal, action.name]
            .filter(Boolean)
            .join(" › ")}
        </p>

        {query.isPending ? (
          <p className="p-6 text-center text-sm text-muted-foreground">
            {t(`${ns}.loading`)}
          </p>
        ) : query.isError || !payload ? (
          <p className="p-6 text-center text-sm text-destructive">
            {t(`${ns}.error`)}
          </p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              {!payload.reorderable
                ? payload.empty_message
                : !pending
                  ? t(`${ns}.linkOrder.intro`)
                  : pending.is_mine
                    ? t(`${ns}.linkOrder.introMine`)
                    : t(`${ns}.linkOrder.introReview`)}
            </p>

            {payload.link_change_pending && (
              <p className="rounded-md border border-warning/40 bg-warning/10 p-2.5 text-xs text-foreground">
                {t(`${ns}.linkOrder.linkChangeWarning`)}
              </p>
            )}

            {pending?.note && (
              // Why the previous order was rejected — required when
              // counter-proposing, so whoever reviews next has to see it.
              <p className="rounded-md border border-border bg-muted/40 p-2.5 text-sm italic text-muted-foreground">
                <span className="mr-1.5 text-[10px] font-semibold uppercase not-italic tracking-wide">
                  {t(`${ns}.linkOrder.note`)}
                </span>
                {pending.note}
              </p>
            )}

            <ol className="flex flex-col gap-2">
              {(pending && !countering
                ? pending.rows.map((row) => ({
                    row,
                    item: payload.items.find((i) => i.id === row.id),
                  }))
                : items.map((item) => ({ row: null, item }))
              ).map((entry, index) => (
                <li
                  key={entry.item?.id ?? entry.row?.id}
                  draggable={editable}
                  onDragStart={() => setDragIndex(index)}
                  onDragEnd={() => setDragIndex(null)}
                  onDragOver={(e) => {
                    if (!editable || dragIndex === null) return;
                    e.preventDefault();
                    if (dragIndex !== index) {
                      move(dragIndex, index);
                      setDragIndex(index);
                    }
                  }}
                  className={cn(
                    "grid grid-cols-[auto_1fr_auto] items-start gap-2.5 rounded-md border border-border bg-card p-2.5",
                    dragIndex === index && "opacity-50",
                  )}
                >
                  <span
                    className={cn(
                      "grid size-7 shrink-0 place-items-center rounded text-xs font-bold tabular-nums text-muted-foreground",
                      editable && "cursor-grab",
                    )}
                    aria-hidden
                  >
                    {editable ? <GripVertical className="size-4" /> : index + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="break-words text-sm font-medium">
                      {entry.item?.title ?? entry.row?.title}
                    </p>
                    <p className="break-words text-xs text-muted-foreground">
                      {entry.item?.subtitle}
                    </p>
                  </div>
                  <span className="flex flex-wrap justify-end gap-1.5">
                    {entry.item && !entry.item.visible && (
                      <Badge tone="muted">
                        {t(`${ns}.linkOrder.notVisible`)}
                      </Badge>
                    )}
                    {entry.row?.is_new ? (
                      <Badge tone="new">{t(`${ns}.linkOrder.new`)}</Badge>
                    ) : entry.row?.moved ? (
                      <Badge>
                        {t(`${ns}.linkOrder.was`, { n: entry.row.previous })}
                      </Badge>
                    ) : null}
                  </span>
                </li>
              ))}
            </ol>

            {pending && (
              <p className="text-xs text-muted-foreground">
                {t(`${ns}.linkOrder.proposedBy`, { name: pending.proposed_by })}
                {pending.added_since.length > 0 &&
                  ` · ${t(`${ns}.linkOrder.addedSince`)}`}
              </p>
            )}

            {countering && (
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">
                  {t(`${ns}.linkOrder.noteLabel`)}
                </span>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  placeholder={t(`${ns}.linkOrder.notePlaceholder`)}
                />
              </label>
            )}

            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
              <Button type="button" variant="outline" onClick={onClose}>
                {t(`${ns}.linkOrder.close`)}
              </Button>
              {payload.reorderable && !pending && (
                <Button
                  type="button"
                  disabled={decide.isPending || !changed}
                  onClick={() =>
                    void submit({
                      decision: "propose",
                      ids: items.map((i) => i.id),
                    })
                  }
                >
                  {t(`${ns}.linkOrder.propose`)}
                </Button>
              )}
              {pending && payload.can_review && !countering && (
                <>
                  <Button type="button" variant="outline" onClick={startCounter}>
                    {t(`${ns}.linkOrder.counter`)}
                  </Button>
                  <Button
                    type="button"
                    disabled={decide.isPending}
                    onClick={() =>
                      void submit({
                        decision: "approve",
                        request_id: pending.id,
                      })
                    }
                  >
                    {t(`${ns}.linkOrder.approve`)}
                  </Button>
                </>
              )}
              {countering && pending && (
                <Button
                  type="button"
                  disabled={decide.isPending}
                  onClick={() =>
                    void submit({
                      decision: "counter",
                      request_id: pending.id,
                      ids: items.map((i) => i.id),
                      note,
                    })
                  }
                >
                  {t(`${ns}.linkOrder.submitCounter`)}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function Badge({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone?: "new" | "muted";
}) {
  return (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 text-[11px] font-semibold",
        tone === "new"
          ? "bg-success/15 text-success"
          : tone === "muted"
            ? "bg-warning/20 text-foreground"
            : "bg-muted text-muted-foreground",
      )}
    >
      {children}
    </span>
  );
}
